/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import styled, { keyframes } from 'styled-components';

const triSpinner = keyframes`
  100% {
    transform: rotate(1turn);
  }
`;

const cycleWords = keyframes`
  10% {
    transform: translateY(-105%);
  }
  25% {
    transform: translateY(-100%);
  }
  35% {
    transform: translateY(-205%);
  }
  50% {
    transform: translateY(-200%);
  }
  60% {
    transform: translateY(-305%);
  }
  75% {
    transform: translateY(-300%);
  }
  85% {
    transform: translateY(-405%);
  }
  100% {
    transform: translateY(-400%);
  }
`;

export const LoaderContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  align-self: center;
  margin: 20px 0 10px 0;
`;

export const ModalInnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
`;

export const Spinner = styled.div<{
  useModalLoader?: boolean;
  borderColor?: string;
}>`
  width: ${(props) => (props?.useModalLoader ? '45px' : '56px')};
  height: ${(props) => (props?.useModalLoader ? '45px' : '56px')};
  display: grid;
  border: 4px solid #0000;
  border-radius: 50%;
  border-right-color: ${(props) => props.borderColor ?? '#104382'};
  animation: ${triSpinner} 1s infinite linear;
  &::before,
  &::after {
    content: '';
    grid-area: 1/1;
    margin: 2px;
    border: inherit;
    border-radius: 50%;
    animation: ${triSpinner} 2s infinite;
  }
  &::after {
    margin: 8px;
    animation-duration: 3s;
  }
`;

export const Loader = styled.div<{ useModalLoader?: boolean }>`
  box-sizing: content-box;
  height: 40px;
  display: flex;
  border-radius: 8px;
  margin: ${(props) =>
    props?.useModalLoader ? '0 0 0 20px' : '20px 0 0 10px'};
`;

export const LoadingText = styled.p<{ color?: string }>`
  color: black;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 25px;
  line-height: 1.4;
`;

export const Words = styled.div`
  overflow: hidden;
`;

export const Word = styled.span<{ color?: string }>`
  display: block;
  height: 100%;
  padding-left: 8px;
  color: ${(props) => props.color ?? '#1F2937'};
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 25px;
  animation: ${cycleWords} 5s infinite;
`;

const textAnimation = keyframes`
  0% {
    clip-path: inset(0 100% 0 0);
  }
  50% {
    clip-path: inset(0);
  }
  100% {
    clip-path: inset(0 0 0 100%);
  }
`;

export const AnimatedTextContainer = styled.div<{
  useModalLoader?: boolean;
  frontColor?: string;
  textColor?: string;
}>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: ${(props) =>
    props?.useModalLoader ? '0 0 0 20px' : '20px 0 0 10px'};
  --front-color: ${(props) => props.frontColor ?? '#1F2937'};
  --text-color: ${(props) => props.textColor ?? '#808080'};
`;

export const AnimatedText = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 20px;
  letter-spacing: 0.2px;
  &::before,
  &::after {
    content: attr(data-text);
  }
  &::before {
    color: var(--text-color);
  }
  &::after {
    color: var(--front-color);
    animation: ${textAnimation} 3.6s ease infinite;
    position: absolute;
    left: 0;
  }
`;
